import * as React from 'react';
import isEqual from 'lodash.isequal';

import { PagesWrapper } from './styles';
import { HEADER_HEIGHT, PAGE_PADDING } from '../styles';
import Background from '../../../containers/Editor/Spread/Background';
import Page from '../../../containers/Editor/Page';
import { EditorType } from '../../../containers/Editor/constants';
import UserAgentUtils from '../../../utils/userAgent';

interface SpreadProps {
	editorType: EditorType;
	spread: number[];
	pageWidth: number;
	pageHeight: number;
	zoom: number;
	isHidden: boolean;
	isThumbnailsDisplayed: boolean;
	thumbnailsWidth: number;
	editorContainer: Element | null;
	scripts: string | undefined;
	isAccessibleVersion: boolean;
	currentSpreadIndex: number;
}

interface SpreadPageState {
	containerWidth: number;
	containerHeight: number;
	currentWidth: number;
	currentHeight: number;
	currentScale: number;
}

declare global {
	interface Window {
		visualViewport: EventTarget;
	}
}

const Spread = ({
	editorType,
	spread,
	pageWidth,
	pageHeight,
	zoom,
	isHidden,
	isThumbnailsDisplayed,
	thumbnailsWidth,
	editorContainer,
	scripts,
	isAccessibleVersion,
	currentSpreadIndex,
}: SpreadProps) => {
	const initialState = {
		containerWidth: 0,
		containerHeight: 0,
		currentWidth: pageWidth,
		currentHeight: pageHeight,
		currentScale: zoom,
	};

	const [{
		containerWidth,
		containerHeight,
		currentWidth,
		currentHeight,
		currentScale,
	}, setState] = React.useState<SpreadPageState>(initialState);
	const [loadedPages, setLoadedPages] = React.useState<number[]>([]);
	const container = React.useRef<HTMLDivElement>(null);

	const setPageSize = (): void => {
		const { offsetWidth } = container.current as HTMLDivElement;
		const offsetHeight = window.innerHeight - HEADER_HEIGHT - PAGE_PADDING * 2;
		const isKeyboardAppeared = UserAgentUtils.isMobileApp &&
			offsetWidth === containerWidth &&
			offsetHeight < containerHeight;

		if (isKeyboardAppeared) {
			return;
		}

		let newWidth = isAccessibleVersion ? pageWidth : offsetWidth / spread.length;
		let newScale = isAccessibleVersion ? newWidth : newWidth / pageWidth;
		let newHeight = pageHeight * newScale;

		const isPageHigherThanContainer = newHeight > offsetHeight;

		if (isPageHigherThanContainer) {
			newHeight = offsetHeight;
			newScale = newHeight / pageHeight;
			newWidth = pageWidth * newScale;
		}

		setState({
			containerWidth: offsetWidth,
			containerHeight: offsetHeight,
			currentWidth: newWidth * zoom,
			currentHeight: newHeight * zoom,
			currentScale: newScale * zoom,
		});
	};

	const handlePageLoad = (page: number): void => {
		setLoadedPages(prevPages => [...prevPages, page]);
	};

	React.useEffect(() => {
		if (editorType === EditorType.HTML || isHidden) {
			return;
		}

		setPageSize();

		// Standard window 'resize' event isn't triggered in webview on iOS13, it should be listened on window.visualViewport
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', setPageSize);
		} else {
			window.addEventListener('resize', setPageSize);
		}

		return () => {
			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', setPageSize);
			} else {
				window.removeEventListener('resize', setPageSize);
			}
		};
	}, [spread, pageWidth, pageHeight, zoom, isThumbnailsDisplayed, thumbnailsWidth, isHidden, editorType]);

	React.useEffect(() => {
		if (editorType === EditorType.HTML || isHidden) {
			return;
		}

		setLoadedPages([]);
	}, [editorType, isHidden, spread]);

	React.useEffect(() => {
		if (editorType === EditorType.HTML || isHidden) {
			return;
		}

		const isAllPagesLoaded = isEqual(loadedPages.sort((a, b) => a - b), spread);

		if (!isAllPagesLoaded) {
			return;
		}

		const runScripts = (): void => {
			const pageContentElements = [...container.current!.querySelectorAll('div[data-page] > div')] as HTMLDivElement[];
			const pageContentWithLoadEvent = pageContentElements.find(element => element.hasAttribute('onload'));

			if (pageContentWithLoadEvent && pageContentWithLoadEvent.onload) {
				pageContentWithLoadEvent.onload(new Event('load'));
			}
		};

		if (scripts) {
			runScripts();
		}
	}, [editorType, isHidden, loadedPages, spread, scripts]);

	if (editorType === EditorType.HTML) {
		return (
			<PagesWrapper
				ref={container}
				hidden={isHidden}
				editorType={editorType}
				zoom={zoom}
			>
				<Page
					page={spread[0]}
					width={currentWidth}
					height={currentHeight}
					spreadZoom={zoom}
					pageScale={currentScale}
					isLoaded={loadedPages.includes(spread[0])}
					editorContainer={editorContainer}
				/>
			</PagesWrapper>
		);
	}

	if (isAccessibleVersion) {
		return (
			<PagesWrapper
				ref={container}
				hidden={isHidden}
				editorType={editorType}
				zoom={zoom}
			>
				<Page
					page={currentSpreadIndex + 1}
					editorContainer={editorContainer}
					width={pageWidth * zoom}
					height={pageHeight * zoom}
					spreadZoom={zoom}
					pageScale={currentScale}
					onLoad={handlePageLoad}
					isLoaded={loadedPages.includes(currentSpreadIndex + 1)}
				/>
			</PagesWrapper>
		);
	}

	return (
		<Background
			isHidden={isHidden}
			editorContainer={editorContainer}
		>
			<PagesWrapper
				ref={container}
				hidden={isHidden}
				editorType={editorType}
				zoom={zoom}
			>
				{spread.map(page => (
					<Page
						key={page}
						page={page}
						editorContainer={editorContainer}
						width={currentWidth}
						height={currentHeight}
						spreadZoom={zoom}
						pageScale={currentScale}
						isLoaded={loadedPages.includes(page)}
						onLoad={handlePageLoad}
					/>
				))}
			</PagesWrapper>
		</Background>
	);
};

export default Spread;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Spread/index.tsx